import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost, secureGetPdf } from "../../config/api";
import { useHistory, useLocation } from "react-router-dom";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import OptionsMenu from "../../components/OptionsMenu";
import Rows from "../../components/Rows";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";
import { formatAmount } from "../../utils/format";
import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg";
import DownloadIcon from "../../icons/downloadB.svg";

const STATUS_COLORS = {
  PAID: "#06D6A0",
  UNPAID: "#A8ADC3",
};

const Invoices = () => {
  const [, setInvoicesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    dateRange: {},
    status:  null,
    needle: "",
  });
  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const location = useLocation();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const { state: { role } } = useContext(UserContext);

  const getStatusLabel = (status) => t(`bill_status.${status}`);

  useEffect(() => {
    if (location.state?.showSnackbar) {
      console.log("Snackbar message:", location.state.snackbarMessage);
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;


  const columns = [
    { label: t(`columns.invoiceNumber`), key: "billNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: "Client", key: "clientName"},
    { label: "Numero de projet", key: "projectNumber" },
    { label: "Immatriculation", key: "vehicle" },
    //use formatAmountString from utils/format.js
    { label: "Total HT", key: "priceHT", render: (priceHT) => formatAmount(priceHT) },
    { label: "Total TTC", key: "priceTTC", render: (priceTTC) => formatAmount(priceTTC) },
    { label: t("columns.status"), key: "paymentStatus",
      render: (paymentStatus) => (
        <span className="text-white text-[12px] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
          style={{ backgroundColor: STATUS_COLORS[paymentStatus] || "#A8ADC3" }}
        >
          {getStatusLabel(paymentStatus).toUpperCase()}
        </span>
      )
    },
/*     {
      key: "actions",
      render: (_, item) => (
        <div className="flex items-center justify-end gap-x-4">
          {item?.status !== "SIGNED" && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(item);
            }}
            className="w-8 h-8 flex items-center justify-center bg-[#FFFFFF] rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
            title="Supprimer"
          >
            <img src={DeleteIcon} alt="delete" className="w-5 h-5" />
          </button>
          )}
        </div>
      ),
    }, */
  ];



  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "clientId",
      label: "Client",
      type: "dropdown",
      options: [...clients.map((client) => ({
        value: client.id,
        reference: client.code,
        name: client.name
      }))],
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { value: "PAID", label: t("bill_status.PAID") },
        { value: "UNPAID", label: t("bill_status.UNPAID") },
      ],
    }
  ];

  // dropdowns
  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchDropdowns = async () => {
      try {
        const clientsRes = await secureGet("/clients", showSnackbar, { signal });
        setClients(clientsRes.data || []);
      } catch (e) {
          if (e.name !== "AbortError") console.error(e);
        }
      };

      fetchDropdowns();
      return () => abortController.abort();
    }, [filters?.clientId]);

  const fetchPaginatedInvoices = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      params.append("page", page);
      params.append("size", size);
      if (activeFilters.needle) params.append("needle", activeFilters.needle);
      if (activeFilters.clientId) params.append("clientId", activeFilters.clientId);
      if (activeFilters.startDate) params.append("startDate", activeFilters.startDate);
      if (activeFilters.endDate) params.append("endDate", activeFilters.endDate);
      if (activeFilters.status) params.append("status", activeFilters.status);

      const response = await secureGet(`/bills?${params.toString()}`, showSnackbar, {
        signal,
      });
      console.log("Bills response:", response);

      const { content, totalPages, totalElements } = response.data;
      setInvoicesData(content);
      console.log("Invoices data:", content);
      setCurrentPageData(content);
      setTotalPages(totalPages || 1);
      setTotalElements(totalElements || 0);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching invoices:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedInvoices(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedInvoices(0, pageSize, filters);
    }
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedInvoices(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      dateRange: {},
      status: null,
      needle: "",
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);

    try {
      await secureDelete(`/invoices/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedInvoices(currentPage, pageSize);
      showSnackbar(t(`confirmation.invoiceDelete`), 'success');
    } catch (error) {
      console.error("Error deleting invoice:", error);
      setDeletePopupVisible(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/invoices/details/${item.id}`,
      state: {
        invoice: item,
        filters,
        page: currentPage,
        size: pageSize,
      },
    });
  };


  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/invoices/${item.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${item.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>Drivnest - {t("titles.invoices")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.invoices`)}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <TableLoadingArea loading={loading} minHeight={200}>
        <Rows
          columns={columns}
          data={currentPageData}
          emptyLabel={t('empty.invoices')}
          {...((role === "ROLE_BILLING_AGENT" || role === "ROLE_ADMIN" || role === "ROLE_SERVICE_CONSULTANT") && { onRowClick: (row) => handleDetails(row) })}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Invoices;